/**
 * Skin Quiz Results Module
 *
 * TECHNICAL DEBT NOTE:
 * - This page uses Slick Carousel
 * - Quiz page (skin-quiz.liquid) uses Flickity
 * - Ideally should standardize on one library (Slick recommended since it's more widely used)
 * - Requires careful testing if migrating quiz page to Slick
 */

// Debug flag - set to true for development, false for production
const SKIN_QUIZ_DEBUG = false;

// Helper function for conditional logging
function debugLog(...args) {
  if (SKIN_QUIZ_DEBUG) {
    console.log(...args);
  }
}

function debugWarn(...args) {
  if (SKIN_QUIZ_DEBUG) {
    console.warn(...args);
  }
}

function debugError(...args) {
  console.error(...args);
}

debugLog('Script loaded at:', new Date().toISOString());

// ============================================
// CONSTANTS - Magic strings centralized here
// ============================================

// localStorage keys
const STORAGE_KEYS = {
  QUIZ_DATA: 'skinQuizData',
  QUIZ_PROFILE: 'skinQuizProfile',
  QUIZ_COMPLETED: 'skinQuizCompleted',
  CURRENT_QUESTION: 'skinQuizCurrentQuestion',
  SELECTED_PRODUCTS: 'skinQuizSelectedProducts'
};

// Product field names for routine steps
const PRODUCT_FIELDS = {
  AM_CLEANSER: 'am_cleanser',
  PM_CLEANSER: 'pm_cleanser',
  EYE_CREAM: 'eye_cream',
  MOISTURIZER: 'moisturizer',
  PIGMENT_TREATMENT: 'pigment_treatment',
  NON_PIGMENT_TREATMENT: 'non_pigment_treatment',
  RETINOL: 'retinol',
  RETINOL_ALTERNATIVE: 'retinol_alternative',
  PHYSICAL_UNTINTED: 'physical_untinted',
  PHYSICAL_TINTED: 'physical_tinted',
  CHEMICAL_UNTINTED: 'chemical_untinted',
  CHEMICAL_TINTED: 'chemical_tinted',
  NO_PREFERENCE_UNTINTED: 'no_preference_untinted',
  NO_PREFERENCE_TINTED: 'no_preference_tinted'
};

// Skin concerns
const SKIN_CONCERNS = {
  HYPERPIGMENTATION: 'Hyperpigmentation',
  ACNE_PRONE: 'Acne-Prone',
  REDNESS_ROSACEA: 'Redness/Rosacea',
  FINE_LINES: 'Fine Lines/Wrinkles',
  DARK_CIRCLES: 'Dark Circles',
  DEHYDRATION: 'Dehydration',
  UNEVEN_TEXTURE: 'Uneven Texture',
  DULLNESS: 'Dullness'
};

// Sunscreen preferences
const SUNSCREEN_PREFS = {
  TYPES: {
    PHYSICAL: 'Physical',
    CHEMICAL: 'Chemical',
    NO_PREFERENCE: 'NoPreference'
  },
  COLORS: {
    TINTED: 'Tinted',
    UNTINTED: 'Untinted'
  }
};

// Debounce helper function - delays execution until after wait period of no calls
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle helper function - limits execution to once per wait period
function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

  // Non-blocking toast, same treatment the routine builder uses. These messages
  // used to be native alert()s: a browser dialog over the brand that froze the
  // page until dismissed, on a surface where the shopper's next move is to pick
  // a different product.
  function showResultsToast(message, type) {
    let toast = document.getElementById('resultsToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'resultsToast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.style.cssText = [
        'position:fixed', 'left:50%', 'bottom:104px', 'transform:translateX(-50%) translateY(12px)',
        'z-index:120', 'max-width:min(92vw,420px)', 'padding:14px 20px',
        'border-radius:var(--se-radius,8px)', 'font-family:var(--se-font-body,sans-serif)',
        'font-size:14px', 'font-weight:600', 'color:#fff', 'text-align:center',
        'box-shadow:0 8px 24px rgba(26,26,26,.18)', 'opacity:0',
        'transition:opacity .25s ease, transform .25s ease', 'pointer-events:none'
      ].join(';');
      document.body.appendChild(toast);
    }
    toast.style.background = type === 'error' ? '#b3261e' : 'var(--se-success, #2E7D32)';
    toast.textContent = message;
    void toast.offsetWidth;
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
    clearTimeout(showResultsToast._t);
    showResultsToast._t = setTimeout(function () {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(12px)';
    }, 3200);
  }

  // Shared Slick slider configuration.
  // Same carousel as the routine landing pages (assets/routine-page-products.js):
  // 4 cards across on desktop, 3 on tablet, one centred card with a peek of the
  // next on mobile. Kept in step with that component so the two surfaces stay
  // visually identical.
  const SLICK_CONFIG = {
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: window.arrows ? window.arrows.icon_prev : '',
    nextArrow: window.arrows ? window.arrows.icon_next : '',
    swipeToSlide: true,
    responsive: [
      {
        breakpoint: 990,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: true
        }
      },
      {
        // Mobile: show the neighbours, not just a hairline of them.
        // Jonathan, 48:00 — "there should be at least one and a half on the
        // page... with the name visible too. I just think that the whole block
        // is a little bit too big." 24px of peek did not read as swipeable at
        // all; 15% on each side puts roughly a third of the next card in view.
        breakpoint: 750,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          centerMode: true,
          centerPadding: '15%'
        }
      }
    ]
  };

  // Initialize a single slider element
  function initSingleSlider($slider) {
    if ($slider.hasClass('slick-initialized')) {
      return; // Already initialized
    }
    if ($slider.children().length === 0) {
      debugLog('Skipping empty slider:', $slider.attr('class'));
      return; // No products to show
    }
    debugLog('Initializing slider:', $slider.attr('class'));
    $slider.slick(SLICK_CONFIG);
  }

  // Function to initialize all product sliders with Slick
  // Uses requestAnimationFrame for better timing instead of arbitrary setTimeout
  function initializeProductSliders() {
    debugLog('Starting Slick slider initialization');

    // Wait for next frame to ensure DOM is settled
    requestAnimationFrame(function() {
      // Double RAF for extra safety (after browser paint)
      requestAnimationFrame(function() {
        const $sliders = $('.rp-product-grid').not('.slick-initialized');

        if ($sliders.length === 0) {
          debugLog('No uninitialized sliders found');
          return;
        }

        $sliders.each(function() {
          initSingleSlider($(this));
        });

        debugLog('Slick sliders initialized:', $sliders.length);
      });
    });
  }

    // Consolidated jQuery document ready handler
    $(document).ready(function() {
      // Quantity button click handler
      $(document).on('click', '.quantity-btn', function(e) {
        // The card root is a <label>. A <button> is interactive content so the
        // spec already stops the label forwarding the click to the checkbox —
        // cancel the default anyway so stepping the quantity can never
        // deselect the product.
        e.preventDefault();
        const input = $(this).siblings('.quantity-input');
        let value = parseInt(input.val());

        if ($(this).hasClass('plus')) {
          value = isNaN(value) ? 1 : Math.min(value + 1, parseInt(input.attr('max')) || value + 1);
        } else if ($(this).hasClass('minus')) {
          value = isNaN(value) ? 1 : Math.max(value - 1, parseInt(input.attr('min')) || 1);
        }

        input.val(value).trigger('change');

        // Update total price when quantity changes
        updateTotalPrice();
      });

      // Quantity input change handler
      $(document).on('change', '.quantity-input', function() {
        let value = parseInt($(this).val());
        const min = parseInt($(this).attr('min')) || 1;
        const max = parseInt($(this).attr('max')) || Infinity;

        if (isNaN(value) || value < min) {
          value = min;
        } else if (value > max) {
          value = max;
        }

        $(this).val(value);
      });

      // Prevent non-numeric input in quantity fields
      $(document).on('keydown', '.quantity-input', function(e) {
        if ($.inArray(e.keyCode, [46, 8, 9, 27, 13]) !== -1 ||
            (e.keyCode == 65 && e.ctrlKey === true) ||
            (e.keyCode >= 35 && e.keyCode <= 39)) {
          return;
        }
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
          e.preventDefault();
        }
      });

      // Custom input checkbox click handler
      $(document).on('click', '.custom-input', function() {
        $(this).parent().parent().find(".quantity-input").val(1);
        $('body').find(".custom_active").removeClass("custom_active");

        if ($(this).prop('checked')) {
          $(this).parent().find(".quantity-input").addClass("custom_active");
        }

        updateTotalPrice();
      });

      // Add to cart / checkout button handler (rebound from the non-existent .button-custom).
      $(document).on('click', '.ebc-btn-primary, .checkout-btn', function(){
        var productsToAdd = [];
        var $button = $(this);
        var isCheckout = $button.hasClass('checkout-btn');
        var originalText = $button.text();

        // One line per variant. A product recommended for both AM and PM has a
        // checkbox in each grid and both are ticked — pushing it twice made
        // Shopify merge the lines and silently double the quantity.
        var seenForCart = {};
        $('.custom-input').each(function() {
          if ($(this).prop('checked')) {
            var ProVariantVal = $(this).val();
            if (seenForCart[ProVariantVal]) { return; }
            seenForCart[ProVariantVal] = true;
            // Read the card's own stepper. This used to read `.custom_active`,
            // but that marker is stripped from the whole page on every card
            // click and re-added only to the card just clicked — so every other
            // selected product went into the cart at quantity 1 no matter what
            // the shopper had set.
            var qt = $(this).closest('label').find('.quantity-input').val() || 1;
            productsToAdd.push({
              id: ProVariantVal,
              quantity: parseInt(qt)
            });
          }
        });

        if (productsToAdd.length === 0) {
          // Say so. Tapping ADD TO CART with an empty routine used to do nothing
          // at all — no message, no movement — which reads as a broken button.
          debugLog("[SKIN-QUIZ-NEW] No products selected");
          showResultsToast('Choose a product from a step below to start your routine.', 'error');
          return;
        }

        // Disable button during request
        $button.prop('disabled', true).text('Adding...');

        $.ajax({
          type: 'POST',
          url: '/cart/add.js',
          data: JSON.stringify({ items: productsToAdd }),
          contentType: 'application/json',
          dataType: 'json',
          success: function(response) {
            debugLog("[SKIN-QUIZ-NEW] Products added to cart:", response);
            // Straight to checkout. No bundle discount code is applied any
            // more (the Bundle20 mechanic was retired in Oct 2026).
            if (isCheckout) {
              window.location.href = '/checkout';
              return;
            }
            $button.text('Added!');
            setTimeout(function() {
              $button.prop('disabled', false).text(originalText);
            }, 1500);
            // Open the cart sidebar
            if (typeof Shopify !== 'undefined' && typeof Shopify.getCart === 'function') {
              Shopify.getCart(function(cart) {
                $('body').addClass('cart-sidebar-show');
                if (typeof halo !== 'undefined' && typeof halo.updateSidebarCart === 'function') {
                  halo.updateSidebarCart(cart);
                }
              });
            }
          },
          error: function(xhr) {
            console.error("[SKIN-QUIZ-NEW] Error adding to cart:", xhr);
            $button.prop('disabled', false).text(originalText);
            var serverMsg = '';
            try {
              var body = xhr.responseJSON || JSON.parse(xhr.responseText || '{}');
              serverMsg = body.description || body.message || '';
            } catch (e) {}
            if (/sold out|unavailable|out of stock/i.test(serverMsg)) {
              // A product went out of stock after the page loaded. Deselect it so
              // the shopper can pick another, and tell them exactly what happened.
              var m = serverMsg.match(/'([^']+)'/);
              var name = m ? m[1] : null;
              if (name) {
                $('.custom-input:checked').each(function () {
                  var t = $(this).closest('label').find('.rp-product-card__title, .custom-title, .ebc-product-card__title, .custom-content-price p').first().text().trim();
                  if (t === name) {
                    this.checked = false;
                    // Dispatch a REAL change event. jQuery's .trigger('change')
                    // only runs jQuery handlers, and everything that recalculates
                    // the total, the step badges and the saved selection listens
                    // via addEventListener — so the sold-out product came off the
                    // card while the price the shopper saw kept counting it.
                    this.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                });
              }
              showResultsToast('Sorry, this product just went out of stock — please select another.', 'error');
            } else {
              showResultsToast('Something went wrong adding your bundle to the cart. Please try again.', 'error');
            }
          }
        });

        debugLog("[SKIN-QUIZ-NEW] Products to add:", productsToAdd);
      });
    });

    // Helper function to update total price display
    function updateTotalPrice() {
      var symbol = '$';
      var seenVariants = {};
      var units = [];

      $('.custom-input:checked').each(function() {
        var variantId = $(this).val();
        var price = parseFloat($(this).attr('product-price')) || 0;
        var qty = parseInt($(this).parent().find('.quantity-input').val()) || 1;

        // Only count price once per variant (same product in AM/PM)
        if (!seenVariants[variantId]) {
          seenVariants[variantId] = true;
          units.push({
            priceCents: price,
            quantity: qty,
            eligible: $(this).attr('data-bundle-eligible') !== 'false'
          });
        }

        // Get currency symbol from data attribute
        var currencyAttr = $(this).attr('data-currency');
        if (currencyAttr) symbol = currencyAttr;
      });

      // Render through the same helper refreshAllSkincareStepUI uses. This
      // function runs on the CLICK and that one on the CHANGE that follows, so
      // writing a plain total here would flash the un-slashed price first.
      var totals = window.SkinExp.bundleTotals(units, getBundleConfig());
      $('.custom-total').each(function () {
        window.SkinExp.renderBundleTotal(this, totals, { symbol: symbol });
      });
      window.SkinExp.renderBundleTotal(document.getElementById('bottomBarTotalPrice'), totals, { symbol: symbol });

      // Update button state
      if ($('.custom-input:checked').length > 0) {
        $(".button-custom").css({
          "background-color": "#000",
          "color": "#fff"
        });
      } else {
        $(".button-custom").css({
          "background-color": "#ccc",
          "color": "#000"
        });
      }
    }

    // Single-check behavior: only one product can be selected per step
    // Using event delegation instead of 100 individual handlers
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('single-check')) {
        const checkbox = e.target;
        if (checkbox.checked) {
          // Find the parent slider container (AM or PM), not the whole step
          const custombox = checkbox.closest('.rp-product-grid') || checkbox.closest('[class*="custombox"]');
          if (custombox) {
            // Uncheck all other single-check checkboxes in the same slider
            custombox.querySelectorAll('.single-check').forEach(otherCheckbox => {
              if (otherCheckbox !== checkbox) {
                otherCheckbox.checked = false;
                // Also hide the quantity selector for unchecked items
                const label = otherCheckbox.closest('label');
                if (label) {
                  const qtySelector = label.querySelector('.quantity-selector');
                  if (qtySelector) qtySelector.style.display = 'none';
                }
              }
            });
          }
        }
      }
    });

    // Function to initialize slick sliders.
    // Uses the one shared SLICK_CONFIG so this path can never drift from the
    // carousel the rest of the page (and the routine landing pages) render.
    function initializeSliders() {
      debugLog(' Initializing sliders');
      $('.rp-product-grid').not('.slick-initialized').each(function () {
        if ($(this).children('label').length > 0) {
          $(this).slick(SLICK_CONFIG);
        }
      });
    }

    // Every step starts open so the shopper can see what is on offer.
    document.querySelectorAll('.accordion-custom-item').forEach(function (item) {
      item.classList.add('active');
    });
    document.querySelectorAll('.accordion').forEach(function (h) { h.classList.add('active'); });

    // Header toggles its step. Open/closed is the `.active` class on the item —
    // no inline maxHeight, which used to be overridden by an !important rule and
    // made the header inert.
    document.addEventListener('click', function (e) {
      var header = e.target.closest('.accordion');
      if (!header) return;
      // The Day/Night control lives in the header; it must not fold the step.
      if (e.target.closest('.day-night-toggle')) return;
      var item = header.closest('.accordion-custom-item');
      if (!item) return;
      setStepOpen(item, !item.classList.contains('active'));
    });
  
  // Bundle offer ("add 3 and save 20%"). Config comes from the theme editor via
  // data-bundle-* on the overlay, so the threshold, the percentage and the code
  // are editable without a deploy. No code configured = nothing shown.
  // Cache only once an offer is actually found. Caching the "no offer" answer
  // would freeze the page into it if the section re-renders — which the theme
  // editor does on every settings change.
  var bundleConfig = null;
  function getBundleConfig() {
    if (bundleConfig && bundleConfig.enabled) return bundleConfig;
    bundleConfig = (window.SkinExp && window.SkinExp.readBundleConfig)
      ? window.SkinExp.readBundleConfig(document)
      : { enabled: false };
    return bundleConfig;
  }

  function updateBundleOffer(distinctCount) {
    var cfg = getBundleConfig();
    var line = document.getElementById('bundleOfferLine');
    if (!line) return;
    if (!cfg.enabled) { line.hidden = true; return; }

    var progress = window.SkinExp.bundleProgress(distinctCount, cfg);
    line.textContent = window.SkinExp.bundleMessage(progress);
    line.classList.toggle('is-unlocked', progress.qualifies);
    // Visible from the start. It used to hide until the first selection, which
    // kept the offer from the people who most needed to hear it.
    line.hidden = false;
  }

  // Open or close one step, and re-measure any carousel inside it — Slick
  // computes slide widths from a visible container, so a grid laid out while
  // the panel was collapsed comes back at zero width without this.
  function setStepOpen(item, open) {
    if (!item) return;
    item.classList.toggle('active', open);
    var header = item.querySelector('.accordion');
    if (header) {
      header.classList.toggle('active', open);
      header.setAttribute('aria-expanded', String(open));
    }
    if (open && window.jQuery && jQuery.fn.slick) {
      setTimeout(function () {
        jQuery(item).find('.rp-product-grid.slick-initialized').each(function () {
          if (jQuery(this).is(':visible')) { jQuery(this).slick('setPosition'); }
        });
      }, 340); // after the max-height transition
    }
  }
  window.__ebcSetStepOpen = setStepOpen;

  // Once every morning step has a product, move the shopper on to the evening
  // routine instead of leaving them on a screen where there is nothing left to
  // do. Fires at most once: if they switch back to Day themselves, that is a
  // decision, and nagging them back to Night would be worse than not helping.
  var autoAdvancedToPM = false;
  function maybeAdvanceToPM() {
    if (autoAdvancedToPM) return;

    var amSteps = 0;
    var amChosen = 0;
    document.querySelectorAll('.accordion-custom').forEach(function (step) {
      // Only steps that are actually part of the morning routine. Retinol is
      // evening-only, so waiting for it here would mean never advancing.
      if (step.getAttribute('data-am') === 'false') return;
      var amGrid = step.querySelector('.rp-product-grid.am-products');
      if (!amGrid || !amGrid.querySelector('input.custom-input')) return;
      amSteps++;
      if (amGrid.querySelector('input.custom-input:checked')) amChosen++;
    });
    if (amSteps === 0 || amChosen < amSteps) return;

    // Only meaningful if there is an evening routine to advance TO.
    var hasPM = Array.prototype.some.call(
      document.querySelectorAll('.rp-product-grid.pm-products'),
      function (g) { return g.querySelector('input.custom-input'); }
    );
    if (!hasPM) return;

    var pmButton = document.querySelector(
      '.ebc-ampm-toggle__option[data-time="pm"], .ampm-btn[data-time="pm"]'
    );
    if (!pmButton || pmButton.classList.contains('is-active') || pmButton.classList.contains('active')) return;

    autoAdvancedToPM = true;
    // Say why the products changed underneath them.
    showResultsToast('Morning routine set — here is your evening one.', 'success');
    setTimeout(function () { pmButton.click(); }, 450);
  }

  // The grid a step is currently SHOWING. A step holds an AM grid and a PM grid
  // and hides one of them, so "what has this step got chosen" only means
  // anything relative to the one on screen.
  function visibleStepGrid(item) {
    if (!item) return null;
    var grids = item.querySelectorAll('.rp-product-grid');
    for (var i = 0; i < grids.length; i++) {
      if (getComputedStyle(grids[i]).display !== 'none') return grids[i];
    }
    return null;
  }

  // What the shopper has picked in the grid they can actually see.
  function visibleStepChoice(item) {
    var grid = visibleStepGrid(item);
    // If neither grid is showing (mid-toggle), fall back to the whole step so
    // this never reports "nothing chosen" for a step that plainly has something.
    return grid
      ? grid.querySelector('input.custom-input:checked')
      : (item ? item.querySelector('input.custom-input:checked') : null);
  }

  // Collapse a step once the shopper has chosen from it, and name the choice in
  // the header so a folded step still says what is in it. Re-opens if they
  // clear the step.
  //
  // Judged against the VISIBLE grid. It used to look at the whole step, so a
  // morning pick kept the step folded after switching to evening — where the
  // shopper had chosen nothing yet and had a decision waiting behind a closed
  // header.
  function syncStepCollapse(checkboxOrItem) {
    var item = !checkboxOrItem ? null
      : (checkboxOrItem.closest ? checkboxOrItem.closest('.accordion-custom-item') : null);
    if (!item && checkboxOrItem && checkboxOrItem.classList
        && checkboxOrItem.classList.contains('accordion-custom-item')) {
      item = checkboxOrItem;
    }
    if (!item) return;

    // A step that belongs to the other routine has no decision waiting in it,
    // so leave it folded rather than opening onto an explanatory sentence.
    var stepEl = item.closest('.accordion-custom');
    if (stepEl && stepEl.classList.contains('is-unavailable')) {
      var staleBadge = item.querySelector('.ebc-step-chosen');
      if (staleBadge) { staleBadge.remove(); }
      setStepOpen(item, false);
      return;
    }

    var chosen = visibleStepChoice(item);
    var header = item.querySelector('.accordion h2');
    if (header) {
      var badge = header.querySelector('.ebc-step-chosen');
      if (chosen) {
        var label = chosen.closest('label');
        var title = label && label.querySelector('.rp-product-card__title');
        var name = title ? title.textContent.trim() : '';
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'ebc-step-chosen';
          header.appendChild(badge);
        }
        badge.textContent = name;
      } else if (badge) {
        badge.remove();
      }
    }
    setStepOpen(item, !chosen);
  }

  // Mark the steps that are not part of the routine currently on screen.
  //
  // Sunscreen is morning-only and Retinol is evening-only, so in the opposite
  // view they have nothing to offer. Rather than hide them — which would make
  // the routine look shorter than it is and renumber everything — say so: the
  // step shows a line explaining which routine it belongs to, and its sidebar
  // entry reads "AM only" / "PM only" instead of a count and stops being a jump
  // target.
  var currentTimeOfDay = 'am';
  function applyTimeOfDayAvailability(time) {
    currentTimeOfDay = (time === 'pm') ? 'pm' : 'am';
    var belongsTo = currentTimeOfDay === 'am' ? 'evening' : 'morning';
    var navTag = currentTimeOfDay === 'am' ? 'PM only' : 'AM only';

    document.querySelectorAll('.accordion-custom').forEach(function (stepEl) {
      var available = stepEl.getAttribute('data-' + currentTimeOfDay) !== 'false';
      stepEl.classList.toggle('is-unavailable', !available);

      var content = stepEl.querySelector('.accordion-custom-content');
      var note = stepEl.querySelector('.ebc-step-unavailable');
      if (!available) {
        if (!note && content) {
          note = document.createElement('p');
          note.className = 'ebc-step-unavailable';
          content.insertBefore(note, content.firstChild);
        }
        if (note) { note.textContent = 'This step is part of your ' + belongsTo + ' routine.'; }
        // Nothing here to choose from, so show no grid at all rather than the
        // other routine's products.
        stepEl.querySelectorAll('.rp-product-grid').forEach(function (g) { g.style.display = 'none'; });
      } else if (note) {
        note.remove();
      }

      var nav = document.querySelector('.ebc-step-nav__item[data-step="' + stepEl.getAttribute('data-step') + '"]');
      if (nav) {
        nav.classList.toggle('is-unavailable', !available);
        nav.disabled = !available;
        var count = nav.querySelector('.ebc-step-nav__count');
        if (count) {
          count.classList.toggle('is-tag', !available);
          if (!available) { count.textContent = navTag; }
        }
      }
    });
  }

  // Re-judge every step. Called after an AM/PM switch, because which grid is
  // visible — and which steps even apply — has just changed for all of them.
  function resyncAllStepCollapse() {
    var pmOn = document.querySelector('.ebc-ampm-toggle__option[data-time="pm"]');
    applyTimeOfDayAvailability(pmOn && pmOn.classList.contains('is-active') ? 'pm' : 'am');
    document.querySelectorAll('.accordion-custom-item').forEach(function (item) {
      syncStepCollapse(item);
    });
    if (typeof refreshAllSkincareStepUI === 'function') { refreshAllSkincareStepUI(); }
  }
  window.__ebcResyncStepCollapse = resyncAllStepCollapse;

  // Publish the pinned header's real height so the step snap can clear it.
  //
  // The AM/PM block is position:sticky at the top of .ebc-routine-content, and
  // the steps snap to `start` in the same scroller — without this a snapped
  // step parks under the header with its heading hidden. The height is not a
  // constant: it grows with a long first name and with the "why these picks"
  // block, so measure the element instead of guessing a pixel value.
  function publishStickyHeight() {
    var header = document.querySelector('.ebc-routine-content .results-header');
    var scroller = document.querySelector('.ebc-routine-content');
    if (!header || !scroller) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) { scroller.style.setProperty('--ebc-sticky-h', h + 'px'); }
  }
  window.__ebcPublishStickyHeight = publishStickyHeight;

  if (typeof ResizeObserver === 'function') {
    var ro = new ResizeObserver(publishStickyHeight);
    var watch = function () {
      var header = document.querySelector('.ebc-routine-content .results-header');
      if (header) { ro.observe(header); publishStickyHeight(); }
    };
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', watch); }
    else { watch(); }
  } else {
    window.addEventListener('load', publishStickyHeight);
    window.addEventListener('resize', publishStickyHeight);
  }
  window.__ebcApplyTimeOfDay = applyTimeOfDayAvailability;

  // Helper: compare a metaobject field (may be array or string) with a string value
  function fieldMatchesValue(fieldValue, searchValue) {
    if (Array.isArray(fieldValue)) {
      return fieldValue.includes(searchValue);
    }
    return fieldValue === searchValue;
  }

  // Helper: check if a routine has real products for a given field
  function routineHasProducts(routine, field) {
    var val = routine.products && routine.products[field];
    if (!val) return false;
    if (Array.isArray(val) && val.length > 0) return true;
    if (typeof val === 'string' && val !== '[]' && val !== '') return true;
    return false;
  }

  // Helper: normalize a product field value (handle double-encoded strings)
  function normalizeProductField(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try { var parsed = JSON.parse(val); return Array.isArray(parsed) ? parsed : []; } catch(e) { return []; }
    }
    return [];
  }

  // Helper function to show user-friendly error message
  function showRoutineError(message) {
    const stepsContainer = document.getElementById('routine-steps-container');
    if (stepsContainer) {
      stepsContainer.innerHTML = `
        <div class="routine-error" style="text-align: center; padding: 40px 20px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #856404; margin-bottom: 10px;">Unable to Load Your Routine</h3>
          <p style="color: #856404; margin-bottom: 20px;">${escapeHtml(message)}</p>
          <a href="/pages/skin-type-quiz" class="button" style="background: #000; color: #fff; padding: 12px 24px; text-decoration: none; display: inline-block;">Retake the Quiz</a>
        </div>
      `;
    }
  }

  // Function to load product data from metaobject and create steps
  function loadProductData() {
    try {
      // Check if the data element exists
      const dataElement = document.getElementById('routineProductsData');
      if (!dataElement) {
        debugError('routineProductsData element not found in DOM');
        showRoutineError('Product data could not be loaded. Please try refreshing the page.');
        return;
      }

      // Try to parse the JSON data
      let routineProductsData;
      try {
        routineProductsData = JSON.parse(dataElement.textContent);
      } catch (parseError) {
        debugError('Failed to parse routineProductsData JSON:', parseError);
        showRoutineError('Product data is corrupted. Please try refreshing the page.');
        return;
      }

      // Validate the data structure
      if (!routineProductsData || typeof routineProductsData !== 'object') {
        debugError('routineProductsData is not a valid object');
        showRoutineError('Product data is in an unexpected format.');
        return;
      }

      if (!routineProductsData.routines || !Array.isArray(routineProductsData.routines)) {
        debugError('routineProductsData.routines is not an array');
        showRoutineError('No skincare routines are available.');
        return;
      }

      if (routineProductsData.routines.length === 0) {
        debugError('routineProductsData.routines is empty');
        showRoutineError('No skincare routines have been configured yet.');
        return;
      }

      // Load skin quiz data first
      const skinProfile = loadSkinQuizData();
      let selectedRoutine = null;

      // No profile means this page has nothing to personalise. It used to fall
      // through to routines[0] and present a stranger's routine under
      // "EXCLUSIVELY CURATED FOR / Your Routine". Send them to the quiz instead —
      // that is the only thing that can make this page true.
      if (!skinProfile) {
        debugLog('No skin profile — redirecting to the quiz');
        window.location.replace('/pages/skin-type-quiz');
        return;
      }

      // Try to find a matching routine based on skin profile
      if (skinProfile) {
        for (const routine of routineProductsData.routines) {
          if (fieldMatchesValue(routine.skin_type, skinProfile.skinType) &&
              fieldMatchesValue(routine.sensitivity, skinProfile.sensitivity) &&
              fieldMatchesValue(routine.concern, skinProfile.primaryConcern)) {
            selectedRoutine = routine;
            break;
          }
        }

        // If no exact match, try matching just skin type
        if (!selectedRoutine) {
          for (const routine of routineProductsData.routines) {
            if (fieldMatchesValue(routine.skin_type, skinProfile.skinType)) {
              selectedRoutine = routine;
              break;
            }
          }
        }
      }

      // Default to first routine if no match found
      if (!selectedRoutine && routineProductsData.routines.length > 0) {
        selectedRoutine = routineProductsData.routines[0];
        debugLog('Using first routine as fallback - no exact match for skin profile');
      }

      if (!selectedRoutine) {
        debugError('No routine could be selected');
        showRoutineError('We couldn\'t find a routine matching your skin profile. Please retake the quiz.');
        return;
      }
      
      // Define the standard skincare routine steps
      const routineSteps = [
        { number: 1, name: 'Cleanser', am: 'am_cleanser', pm: 'pm_cleanser' },
        { number: 2, name: 'Eye Cream', am: 'eye_cream', pm: 'eye_cream' },
        { number: 3, name: 'Treatment', am: 'non_pigment_treatment', pm: 'non_pigment_treatment' },
        { number: 4, name: 'Moisturizer', am: 'moisturizer', pm: 'moisturizer' },
        { number: 5, name: 'Sunscreen', am: 'physical_untinted', pm: null }
      ];
      
      // Customize treatment field if user has pigmentation concerns
      if (skinProfile && skinProfile.primaryConcern === 'Hyperpigmentation') {
        const treatmentStep = routineSteps.find(step => step.name === 'Treatment');
        if (treatmentStep) {
          treatmentStep.am = 'pigment_treatment';
          treatmentStep.pm = 'pigment_treatment';
        }
      }
      
      // Customize sunscreen field based on preferences
      if (skinProfile && skinProfile.sunscreenPrefs) {
        const sunscreenStep = routineSteps.find(step => step.name === 'Sunscreen');
        if (sunscreenStep) {
          if (skinProfile.sunscreenPrefs.type === 'Physical' && skinProfile.sunscreenPrefs.color === 'Untinted') {
            sunscreenStep.am = 'physical_untinted';
          } else if (skinProfile.sunscreenPrefs.type === 'Physical' && skinProfile.sunscreenPrefs.color === 'Tinted') {
            sunscreenStep.am = 'physical_tinted';
          } else if (skinProfile.sunscreenPrefs.type === 'Chemical' && skinProfile.sunscreenPrefs.color === 'Untinted') {
            sunscreenStep.am = 'chemical_untinted';
          } else if (skinProfile.sunscreenPrefs.type === 'Chemical' && skinProfile.sunscreenPrefs.color === 'Tinted') {
            sunscreenStep.am = 'chemical_tinted';
          } else if (skinProfile.sunscreenPrefs.type === 'NoPreference' && skinProfile.sunscreenPrefs.color === 'Untinted') {
            sunscreenStep.am = 'no_preference_untinted';
          } else if (skinProfile.sunscreenPrefs.type === 'NoPreference' && skinProfile.sunscreenPrefs.color === 'Tinted') {
            sunscreenStep.am = 'no_preference_tinted';
          }
        }
      }
      
      // Modify routine based on pregnancy status
      if (skinProfile && skinProfile.isPregnant) {
        const retinolStep = routineSteps.find(step => step.name === 'Retinol/Retinoid');
        if (retinolStep) {
          retinolStep.pm = 'retinol_alternative';
        } else {
          // Add a special retinol alternative step if pregnant
          routineSteps.splice(4, 0, { 
            number: 4, 
            name: 'Retinol Alternative', 
            am: null, 
            pm: 'retinol_alternative',
            note: 'Pregnancy-safe alternatives to retinol'
          });
          // Adjust numbering for subsequent steps
          for (let i = 4; i < routineSteps.length; i++) {
            routineSteps[i].number = i + 1;
          }
        }
      } else {
        // Add retinol step for non-pregnant users if not already present
        const hasRetinol = routineSteps.some(step => step.name === 'Retinol/Retinoid');
        if (!hasRetinol) {
          routineSteps.splice(4, 0, { 
            number: 4, 
            name: 'Retinol/Retinoid', 
            am: null, 
            pm: 'retinol'
          });
          // Adjust numbering for subsequent steps
          for (let i = 4; i < routineSteps.length; i++) {
            routineSteps[i].number = i + 1;
          }
        }
      }
      
      // Which steps belong to which routine.
      //
      // The data is explicit: Sunscreen is morning-only (pm: null) and
      // Retinol / Retinol Alternative is evening-only (am: null). The rest run
      // twice, and Cleanser runs from two different lists (am_cleanser and
      // pm_cleanser), so the morning and evening routines are genuinely
      // different sets of steps.
      //
      // This used to be flattened — pm was mirrored into am whenever am was
      // null — so the evening-only Retinol step showed its evening products in
      // the morning view and read as part of the morning routine. That was a
      // workaround for the step rendering blank. Keep the distinction and say
      // it in the UI instead.
      routineSteps.forEach(function (step) {
        step.inAM = !!(step.am && routineHasProducts(selectedRoutine, step.am));
        step.inPM = !!(step.pm && routineHasProducts(selectedRoutine, step.pm));
      });

      // Rebuild the sidebar step nav from the real routine steps.
      //
      // This used to emit bare `<div>`s, which dropped everything the rest of
      // the page relies on: the .ebc-step-nav__item class the stylesheet and the
      // click handler both target, and the data-step attribute that says which
      // accordion to scroll to. The result was a step list that looked roughly
      // right and did nothing when clicked, and could not be reached by keyboard
      // at all. Buttons, so Tab and Enter work for free.
      const categoriesContainer = document.getElementById('routine-categories');
      if (categoriesContainer) {
        let categoriesHTML = '';
        routineSteps.forEach((step, index) => {
          const n = index + 1;
          const active = index === 0 ? ' is-active' : '';
          const current = index === 0 ? ' aria-current="step"' : '';
          categoriesHTML +=
            `<button type="button" class="ebc-step-nav__item${active}" data-step="${n}"${current}` +
              ` data-am="${step.inAM}" data-pm="${step.inPM}">` +
              `<span class="ebc-step-nav__label">STEP ${n < 10 ? '0' + n : n}</span>` +
              `<span class="ebc-step-nav__title">${escapeHtml(step.name)}</span>` +
              `<span class="ebc-step-nav__count itemqt itemcount${n}">0</span>` +
            `</button>`;
        });
        categoriesContainer.innerHTML = categoriesHTML;
      }

      // Rebuild the MOBILE step tabs from the same routine steps. They were
      // Liquid-rendered from unrelated section-block collection titles (e.g.
      // "Bath & Shower", "Baby Foot"), which is what showed on mobile. data-step
      // matches the accordions so the delegated tab handler scrolls correctly.
      const mobileTabsContainer = document.querySelector('.ebc-mobile-step-tabs, .mobile-step-tabs');
      if (mobileTabsContainer) {
        let tabsHTML = '';
        routineSteps.forEach((step, index) => {
          const active = index === 0 ? ' is-active' : '';
          const current = index === 0 ? ' aria-current="step"' : '';
          tabsHTML += `<button class="mobile-step-tab${active}" data-step="${index + 1}"${current}>${step.number}. ${step.name}</button>`;
        });
        mobileTabsContainer.innerHTML = tabsHTML;
      }

      // Global event handlers - only attach once
      let handlersInitialized = false;
      let isProcessingChange = false; // Flag to prevent double processing of change events

      function initializeHandlers() {
        if (handlersInitialized) {
          debugLog(' Handlers already initialized, skipping');
          return;
        }

        debugLog(' Initializing event handlers ONCE');
        handlersInitialized = true;

        // QUANTITY BUTTONS - Delegate to document level
        document.addEventListener('click', function(event) {
          let quantityBtn = event.target.closest('.quantity-btn');

          if (quantityBtn) {
            debugLog(' Quantity button clicked', quantityBtn);
            event.preventDefault();
            event.stopPropagation();
            
            const quantitySelector = quantityBtn.closest('.quantity-selector');
            const input = quantitySelector?.querySelector('.quantity-input');
            
            if (!input) {
              debugWarn(' No quantity input found for quantity button.');
              return;
            }
            
            let value = parseInt(input.value) || 1;
            if (quantityBtn.classList.contains('plus')) {
              value = Math.min(value + 1, 99);
            } else {
              value = Math.max(value - 1, 1);
            }
            input.value = value;

            // After quantity change, use debounced refresh for better performance
            // when rapidly clicking +/- buttons
            debouncedRefreshUI();

          } else {
              // Check if the click was on an interactive element *within* the product card first
              const isInteractiveElement = event.target.closest('.quantity-selector, .closebtn') || 
                                           event.target.tagName === 'INPUT' || 
                                           event.target.tagName === 'BUTTON'; // Also check for BUTTON tag explicitly

              const productLabel = event.target.closest('label'); // Find the parent label
              const checkbox = productLabel ? productLabel.querySelector('.custom-input') : null;

              if (!isInteractiveElement && productLabel && checkbox) {
                  // If the click was on a label containing a checkbox, and not an interactive element,
                  // log it and let the browser's default action proceed.
                  // The browser will toggle the checkbox and fire a 'change' event.
                  debugLog(' Click on label area detected, allowing default action for:', productLabel);
                  // No preventDefault() or stopPropagation() here, to allow natural behavior.
              } else if (!isInteractiveElement) {
                  // Click was not on an interactive element, nor a recognized product label with a checkbox.
                  debugLog(' Click detected on non-interactive, non-label area or label without checkbox.', event.target);
              } else {
                  // Click was on a recognized interactive element.
                  debugLog(' Click detected on an interactive element, letting default/other handlers manage.', event.target);
              }
          }
        }, true);

        // CHECKBOX CHANGES - Delegate to document level
        document.addEventListener('change', function(event) {
          if (event.target.classList && event.target.classList.contains('custom-input')) {
            debugLog(' Checkbox changed (event target):', event.target);

            // Prevent re-entrancy / double processing
            if (isProcessingChange) {
                debugLog(' Change event ignored (already processing)');
                return;
            }
            isProcessingChange = true;
            debugLog(' Processing change event...');
            
            const changedCheckbox = event.target;
            
            const label = changedCheckbox.closest('label');
            const quantitySelector = label?.querySelector('.quantity-selector');
            
            if (!label || !quantitySelector) {
                debugWarn(' Could not find label or quantity selector for changed checkbox.', changedCheckbox);
                 // Reset flag even if elements not found
                 setTimeout(() => {
                     debugLog(' Resetting change processing flag (early exit).');
                     isProcessingChange = false;
                 }, 0);
                refreshAllSkincareStepUI(); // Still refresh UI 
                return; // Exit early if essential elements are missing
            }
            
            if (changedCheckbox.checked) {
                debugLog(' Checkbox is now checked:', changedCheckbox.value);
                quantitySelector.style.display = 'flex';
                
                // ---- Single selection logic ----
                const name = changedCheckbox.name;
                const sliderContainer = changedCheckbox.closest('.rp-product-grid');
                if (sliderContainer) {
                    sliderContainer.querySelectorAll(`.custom-input[name="${name}"]`).forEach(otherCheckbox => {
                        if (otherCheckbox !== changedCheckbox && otherCheckbox.checked) {
                            debugLog(' (Change Handler) Unchecking other checkbox:', otherCheckbox.value);
                            otherCheckbox.checked = false;
                            const otherLabel = otherCheckbox.closest('label');
                            const otherQuantitySelector = otherLabel?.querySelector('.quantity-selector');
                            if (otherQuantitySelector) {
                                otherQuantitySelector.style.display = 'none';
                            }
                        }
                    });
                }
                // ---- END Single selection logic ----

            } else {
              debugLog(' Checkbox is now unchecked:', changedCheckbox.value);
              quantitySelector.style.display = 'none';
            }

            // ---- Clearing a product clears it everywhere ----
            // A product recommended for both morning and evening has a checkbox
            // in the step's AM grid AND its PM grid. Deselecting the visible
            // card left the hidden twin checked, so the header kept naming the
            // product the shopper had just removed and it stayed in the cart
            // payload — "remove" did not remove.
            //
            // ONLY on uncheck. Mirroring a CHECK too would tick the evening copy
            // the moment a morning product was picked, so every step arrived at
            // the evening routine already answered and folded shut — the shopper
            // never got to choose an evening product at all. Morning and evening
            // are separate choices; the cart merges them because they are one
            // variant, and that is the cart's job, not the page's.
            //
            // Setting .checked in script fires no change event, so this cannot
            // re-enter.
            if (!changedCheckbox.checked) {
              var twinScope = changedCheckbox.closest('.accordion-custom-item');
              if (twinScope) {
                twinScope.querySelectorAll('input.custom-input[value="' + changedCheckbox.value + '"]:checked').forEach(function (twin) {
                  if (twin === changedCheckbox) return;
                  twin.checked = false;
                  var twinQty = twin.closest('label') && twin.closest('label').querySelector('.quantity-selector');
                  if (twinQty) { twinQty.style.display = 'none'; }
                });
              }
            }

            // After all checkbox changes triggered by this initial event are complete, refresh the entire UI state.
            refreshAllSkincareStepUI();

            // Fold the step now that it has an answer (and name the answer in
            // the header), or re-open it if they cleared their choice.
            syncStepCollapse(changedCheckbox);

            // Morning routine finished? Move them on to the evening one.
            maybeAdvanceToPM();

            // Reset the flag shortly after to allow subsequent events
            setTimeout(() => {
                debugLog(' Resetting change processing flag.');
                isProcessingChange = false;
            }, 0);
          }
        }, true);
      }
      
      // Initialize handlers after DOM content is loaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeHandlers);
      } else {
        // DOM already loaded, initialize immediately
        initializeHandlers();
      }

      // Create the steps containers
      const stepsContainer = document.getElementById('routine-steps-container');
      if (stepsContainer) {
        let stepsHTML = '';
        
        routineSteps.forEach((step, index) => {
          const stepId = `step-${step.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
          const hasAM = step.inAM;
          const hasPM = step.inPM;
          
          // Keep the class/attribute contract the Liquid version renders
          // (sections/custom-quiz.liquid). This template dropped `data-step` and
          // the ebc-* aliases, so `.ebc-accordion[data-step="N"]` — the target
          // every step-nav and mobile-tab handler jumps to — matched nothing the
          // moment the steps were rebuilt from the routine metaobject. That is
          // the deepest reason the sidebar step list appeared to do nothing.
          stepsHTML += `
            <div class="accordion-custom ebc-accordion" data-step="${index + 1}" data-am="${hasAM}" data-pm="${hasPM}">
              <div class="accordion-custom-item ebc-accordion__item active">
                <div class="accordion ebc-accordion__header">
                  <h2>${step.number}. ${step.name}</h2>
                  <div class="day-night-toggle" ${step.name === 'Sunscreen' ? 'style="justify-content: flex-start;"' : ''}>
                    <button type="button" class="toggle-btn am-btn ${hasAM ? 'active' : 'disabled'}" onclick="toggleDayNight(this, 'am', '${stepId}')">
                      <svg class="sun-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="5"/>
                        <line x1="12" y1="1" x2="12" y2="3"/>
                        <line x1="12" y1="21" x2="12" y2="23"/>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                        <line x1="1" y1="12" x2="3" y2="12"/>
                        <line x1="21" y1="12" x2="23" y2="12"/>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                      </svg>
                      Day
                    </button>
                    ${step.name !== 'Sunscreen' ? `
                    <button type="button" class="toggle-btn pm-btn ${!hasAM && hasPM ? 'active' : ''}" onclick="toggleDayNight(this, 'pm', '${stepId}')">
                      <svg class="moon-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                      </svg>
                      Night
                    </button>` : ''}
                  </div>
                </div>
                <div class="accordion-custom-content ebc-accordion__content custombox${index + 1} panel">
                  ${step.note ? `<p class="step-note">${step.note}</p>` : ''}
                  <div class="rp-product-grid am-products ${stepId}-am-products" ${!hasAM || hasPM && !hasAM ? 'style="display: none;"' : ''}>
                    <!-- AM products -->
                  </div>
                  <div class="rp-product-grid pm-products ${stepId}-pm-products" ${hasAM || !hasPM ? 'style="display: none;"' : ''}>
                    <!-- PM products -->
                  </div>
                </div>
              </div>
            </div>
          `;
        });
        
        stepsContainer.innerHTML = stepsHTML;
        
        // Now populate products
        routineSteps.forEach((step, index) => {
          const stepId = `step-${step.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
          
          if (step.am) {
            const amContainer = document.querySelector(`.${stepId}-am-products`);
            if (amContainer) {
              populateProducts(selectedRoutine, step.am, stepId + '-am-products');
            }
          }
          
          if (step.pm) {
            const pmContainer = document.querySelector(`.${stepId}-pm-products`);
            if (pmContainer) {
              populateProducts(selectedRoutine, step.pm, stepId + '-pm-products');
            }
          }
        });

        // The page opens on the morning routine, so mark the steps that belong
        // to the evening one before anything else reads their state.
        applyTimeOfDayAvailability('am');

        // Restore any previously-selected products so a page refresh doesn't wipe
        // the shopper's choices (they are saved to localStorage on every change).
        restoreSavedSelections();

        // Initialize Slick sliders after all products are populated
        debugLog(' All products populated, initializing Slick sliders');
        initializeProductSliders();
      }
    } catch (error) {
      debugError('Error loading product data:', error);
      showRoutineError('An unexpected error occurred while loading your routine. Please try refreshing the page.');
    }
  }

  // Re-check the products the shopper had selected before the last refresh.
  function restoreSavedSelections() {
    var saved;
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEYS.SELECTED_PRODUCTS) || '{}'); } catch (e) { return; }
    if (!saved || typeof saved !== 'object') return;
    var restored = 0;
    Object.keys(saved).forEach(function (variantId) {
      var qty = parseInt(saved[variantId], 10) || 1;
      // ONE copy per step, not every copy. A product recommended for both
      // morning and evening has a checkbox in each grid, and ticking both meant
      // a refresh silently answered the evening routine on the shopper's behalf
      // and folded every step shut. What was saved is a cart line, not two
      // separate choices, so restore it once per step — into whichever grid
      // comes first, which is the morning one.
      var perStep = {};
      document.querySelectorAll('.custom-input[value="' + variantId + '"]').forEach(function (cb) {
        var step = cb.closest('.accordion-custom-item');
        var key = step ? (step.querySelector('.accordion h2') || {}).textContent || 'x' : 'x';
        if (perStep[key]) return;
        perStep[key] = true;
        cb.checked = true;
        var label = cb.closest('label');
        var qi = label && label.querySelector('.quantity-input');
        if (qi) { qi.value = qty; }
        restored++;
      });
    });
    if (restored) {
      try {
        if (typeof refreshAllSkincareStepUI === 'function') { refreshAllSkincareStepUI(); }
        else if (typeof updateItemCount === 'function') { updateItemCount(); }
      } catch (e) {}
    }
  }
  
  // Build one product card.
  //
  // This is the SAME card the routine landing pages render
  // (snippets/routine-page-product-card.liquid + assets/routine-page-products.css),
  // in bundle mode: the root is a <label> around a clipped checkbox, so clicking
  // anywhere on the card adds it to the routine. The legacy hooks the rest of
  // this file reads (.custom-input, .single-check, product-price,
  // .quantity-input) ride along on the new markup.
  function buildProductCardHTML(card) {
    const variantId = escapeHtml(card.variantId);
    const productId = escapeHtml(card.productId);
    // Liquid's strip_html leaves entities in place, so the JSON already carries
    // "&amp;". Escaping that again printed a literal "&amp;" on the card
    // ("Obagi's industry-leading &amp; best-selling product"). Decode first,
    // then escape exactly once.
    const title = escapeHtml(decodeEntities(card.title));
    const fieldName = escapeHtml(card.fieldName);
    const imageUrl = escapeHtml(card.imageUrl || '');
    const description = card.description ? escapeHtml(decodeEntities(card.description)) : '';

    // Only show a rating when the product actually has reviews — five filled
    // stars next to "0" would be inventing a rating.
    const ratingCount = parseInt(card.ratingCount, 10) || 0;
    const ratingValue = Math.round(parseFloat(card.rating) || 0);
    let ratingHtml = '';
    if (ratingCount > 0) {
      let starsHtml = '';
      for (let i = 1; i <= 5; i++) {
        starsHtml +=
          '<svg class="rp-product-card__star' + (i > ratingValue ? ' rp-product-card__star--empty' : '') +
          '" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
          '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
      }
      ratingHtml =
        '<div class="rp-product-card__stars">' + starsHtml + '</div>' +
        '<span class="rp-product-card__rating-count">' + ratingCount + '</span>';
    }

    const imageHtml = imageUrl
      ? `<img class="rp-product-card__image" src="${imageUrl}" alt="${title}" width="600" height="600" loading="lazy">`
      : '<div class="rp-product-card__image rp-product-card__image--placeholder"></div>';

    return `
      <label class="rp-product-card" data-product-id="${productId}" data-variant-id="${variantId}" data-price="${escapeHtml(card.priceHtml)}">
        <input type="checkbox" class="rp-product-checkbox custom-input single-check" name="${fieldName}" value="${variantId}" product-price="${card.priceCents}" data-currency="$" data-product-title="${title}" data-product-id="${productId}" data-variant-id="${variantId}" data-bundle-eligible="${card.eligible === false ? 'false' : 'true'}">

        <div class="rp-product-card__image-wrap">
          <span class="rp-product-card__select-overlay" aria-hidden="true">
            <span class="rp-product-card__check-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
          </span>
          ${imageHtml}
        </div>

        <div class="rp-product-card__info">
          <div class="rp-product-card__header">
            <h3 class="rp-product-card__title">${title}</h3>
            <span class="rp-product-card__price">${escapeHtml(card.priceHtml)}</span>
          </div>
          ${description ? `<p class="rp-product-card__description">${description}</p>` : ''}
          <div class="rp-product-card__rating">${ratingHtml}</div>
          <span class="rp-product-card__select-btn" aria-hidden="true">
            <span class="rp-product-card__select-btn-default">Add to Routine</span>
            <span class="rp-product-card__select-btn-added">&#10003; Added</span>
          </span>
          <div class="quantity-selector">
            <button type="button" class="quantity-btn minus" data-currency="$" aria-label="Decrease quantity">-</button>
            <input type="number" class="quantity-input custom_active" value="1" min="1" max="99" aria-label="Quantity">
            <button type="button" class="quantity-btn plus" data-currency="$" aria-label="Increase quantity">+</button>
          </div>
        </div>
      </label>
    `;
  }

  // Function to populate products in the corresponding container
  function populateProducts(routineData, fieldName, containerClass) {
    debugLog(`[SKIN-QUIZ-NEW] Populating products for ${fieldName} in ${containerClass}`, routineData);
    const container = document.querySelector(`.${containerClass}`);
    if (!container) {
      console.error(`[SKIN-QUIZ-NEW] Container ${containerClass} not found`);
      // Debug all available containers
      const allContainers = document.querySelectorAll('[class*="-products"]');
      debugLog(`[SKIN-QUIZ-NEW] Available product containers (${allContainers.length}):`, 
        Array.from(allContainers).map(el => el.className));
      return;
    }
    debugLog(`[SKIN-QUIZ-NEW] Found container for ${containerClass}:`, container);
    
    let productsHtml = '';
    
    // Ensure we have valid product data (handle double-encoded strings from Liquid)
    let products = [];
    if (routineData && routineData.products && routineData.products[fieldName]) {
      products = normalizeProductField(routineData.products[fieldName]);
      // If normalizeProductField returned empty but value is a non-array object, wrap it
      if (products.length === 0 && typeof routineData.products[fieldName] === 'object' && !Array.isArray(routineData.products[fieldName])) {
        products = [routineData.products[fieldName]];
      }
    }
    
    debugLog(`[SKIN-QUIZ-NEW] Processing ${products.length} products for ${fieldName}:`, products);
    
    if (products.length > 0) {
      // Generate HTML for each product
      products.forEach(product => {
        if (!product) return; // Skip if product is null or undefined
        
        // `variants` only appears on the legacy fallback shape; the routine JSON
        // from snippets/routine-variant-json.liquid is already variant-level.
        const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
        const variantId = variant ? variant.id : (product.id || '');

        // Get image URL. The routine JSON carries a plain `image` string; the
        // object shapes are kept for the legacy fallback path.
        let imageUrl = '';
        if (product.image) {
          imageUrl = product.image;
        } else if (product.featured_image && typeof product.featured_image === 'string') {
          imageUrl = product.featured_image;
        } else if (product.featured_image && product.featured_image.src) {
          imageUrl = product.featured_image.src;
        } else if (product.images && product.images.length > 0) {
          imageUrl = typeof product.images[0] === 'string' ? product.images[0] : (product.images[0].src || '');
        }

        // Format price
        let priceHtml = '';
        let price = 0;
        if (variant && variant.price) {
          price = variant.price;
          priceHtml = formatMoney(price);
        } else if (product.price) {
          price = product.price;
          priceHtml = formatMoney(price);
        } else {
          priceHtml = '$0.00';
        }
        
        // The name already carries the product title plus the variant size.
        const productName = variant ? variant.name : (product.name || product.title || 'Product');

        productsHtml += buildProductCardHTML({
          fieldName: fieldName,
          variantId: variantId,
          productId: product.id,
          title: productName,
          imageUrl: imageUrl,
          priceCents: price,
          priceHtml: priceHtml,
          description: product.description || (variant && variant.description) || '',
          rating: product.rating,
          ratingCount: product.rating_count,
          // `elig` from snippets/routine-variant-json.liquid: does the bundle
          // discount cover this product? Absent means yes — the legacy fallback
          // shape has no flag, and hiding a qualifying product from the counter
          // would be the worse error.
          eligible: product.elig !== false
        });
      });
    } else {
      debugWarn(`[SKIN-QUIZ-NEW] No products found for ${fieldName}, using fallback products`);
      // Use fallback products based on the fieldName
      const fallbackProducts = getFallbackProducts(fieldName);
      
      if (fallbackProducts && fallbackProducts.length > 0) {
        fallbackProducts.forEach(product => {
          productsHtml += buildProductCardHTML({
            fieldName: fieldName,
            variantId: product.variantId,
            productId: product.id,
            title: product.size || product.title,
            imageUrl: product.imageUrl,
            priceCents: product.price,
            priceHtml: product.priceFormatted,
            description: product.description || '',
            rating: product.rating,
            ratingCount: product.rating_count
          });
        });
      } else {
        productsHtml = `
          <div class="no-products-message" style="text-align: center; padding: 30px 20px; background: #f5f5f5; border-radius: 8px; margin: 10px 0;">
            <p style="margin: 0; color: #666;">No products currently available for this step.</p>
            <p style="margin: 10px 0 0; font-size: 14px; color: #888;">Products will appear once they're configured in your routine.</p>
          </div>
        `;
        debugLog(`No products or fallbacks available for ${fieldName}`);
      }
    }
    
    // Update the container with product HTML
    debugLog(`[SKIN-QUIZ-NEW] Setting innerHTML for ${containerClass} with ${products.length} products`);
    container.innerHTML = productsHtml;
    
    // Debug what was actually inserted into the DOM
    debugLog(`[SKIN-QUIZ-NEW] After update, container ${containerClass} now contains:`, 
      container.children.length, 'children, first child is:', 
      container.children.length > 0 ? container.children[0].tagName : 'none');
    
    // Initialize event handlers for product cards and quantity buttons
    initProductInteraction(container);
  }
  
  // Function to get fallback products based on the field name
  // NOTE: Removed hardcoded product IDs that could become stale.
  // Products should be configured in Shopify metaobjects.
  // If no products are found, the UI will show "No products available" message.
  function getFallbackProducts(fieldName) {
    debugLog(`No fallback products configured for ${fieldName}`);
    // Return empty array - let the UI handle displaying "No products available"
    return [];
  }
  
  // Function to initialize product interactions
  function initProductInteraction(container) {
    // Add click handlers for quantity buttons - REMOVED, handled by delegated listener
    // container.querySelectorAll('.quantity-btn').forEach(btn => { ... });
    
    // Product cards - REMOVED, handled by delegated listener
    // container.querySelectorAll('.custom-product-card').forEach(card => { ... });
    
    // Checkboxes - REMOVED, handled by delegated listener
    // container.querySelectorAll('.custom-input').forEach(input => { ... });
    
    // Close buttons - REMOVED, handled by delegated listener
    // container.querySelectorAll('.closebtn').forEach(btn => { ... });
    debugLog(`[SKIN-QUIZ-NEW] initProductInteraction called for ${container.className}, but direct handlers are removed (using delegation).`);
  }
  
  // Update the item count in the sidebar and calculate total price
  function updateItemCount() {
    let total = 0;
    const selectedProducts = {};
    const stepQuantities = {}; // E.g., { "1-am": 0, "1-pm": 0, "2": 0, ... }
    const countedVariants = new Set(); // Track variant IDs to avoid double-counting AM/PM
    // { quantity, eligible } per selection, for the bundle offer counter.
    const bundleUnits = [];

    // Initialize all potential step counters to 0 by querying them from the DOM
    // This ensures we only try to update counters that exist.
    document.querySelectorAll('[class*="itemcount"]').forEach(el => {
      const match = el.className.match(/itemcount([\w-]+)/);
      if (match && match[1]) {
        // Ensure we don't overwrite if multiple classes map to same logical key (though unlikely here)
        if (!(match[1] in stepQuantities)) { 
            stepQuantities[match[1]] = 0;
        }
      }
    });
    
    document.querySelectorAll('.custom-input:checked').forEach(checkbox => {
      const accordionCustomItem = checkbox.closest('.accordion-custom-item');
      if (!accordionCustomItem) {
        debugWarn(' Checkbox found without parent .accordion-custom-item:', checkbox);
        return;
      }

      const h2 = accordionCustomItem.querySelector('.accordion h2');
      if (!h2) {
        debugWarn(' .accordion-custom-item found without h2:', accordionCustomItem);
        return;
      }
      
      const stepText = h2.textContent.trim(); // e.g., "1. Cleanser" or "2. Eye Cream"
      const stepNumberMatch = stepText.match(/^(\d+)\./);
      if (!stepNumberMatch) {
        debugWarn(' Step H2 text does not start with number:', stepText);
        return;
      }
      const stepNumber = stepNumberMatch[1];
      const stepName = stepText.substring(stepText.indexOf('.') + 1).trim();

      const price = parseFloat(checkbox.getAttribute('product-price') || 0);
      const quantityInput = checkbox.closest('label')?.querySelector('.quantity-input');
      const quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1; // Default to 1 if NaN or 0

      const variantId = checkbox.value;

      // Count each variant ONCE. A product recommended for both AM and PM has a
      // checkbox in each grid; both get ticked, but it is still one line in the
      // cart. Counting it twice inflated the step badge, and — because the
      // doubled quantity was written straight back to localStorage — made the
      // saved quantity double again on every refresh (1 -> 2 -> 4 -> 8).
      if (countedVariants.has(variantId)) { return; }
      countedVariants.add(variantId);

      total += price * quantity;

      // The sidebar renders ONE count badge per step (.itemcountN), so AM and PM
      // selections for a step must aggregate into the same key. The old cleanser
      // AM/PM suffix produced key "1-am", which matched no .itemcount badge — that
      // is exactly why the Cleanser sidebar count never moved off 0.
      const fullStepKey = `${stepNumber}`;
      if (fullStepKey in stepQuantities) {
        stepQuantities[fullStepKey] += quantity;
      } else {
        stepQuantities[fullStepKey] = quantity;
        debugWarn(`[SKIN-QUIZ-NEW] Encountered new step key during processing: ${fullStepKey}. Initializing.`);
      }

      if (variantId) {
        selectedProducts[variantId] = quantity;
      }

      bundleUnits.push({
        quantity: quantity,
        eligible: checkbox.getAttribute('data-bundle-eligible') !== 'false'
      });
    });
    
    // Update step count displays in the sidebar
    for (const key in stepQuantities) {
        const itemCountElement = document.querySelector(`.itemcount${key}`);
        if (itemCountElement) {
            const currentQuantity = stepQuantities[key];
            itemCountElement.textContent = currentQuantity;
            itemCountElement.style.backgroundColor = currentQuantity > 0 ? 'var(--se-success)' : '#efefef';
            itemCountElement.style.color = currentQuantity > 0 ? '#fff' : '#000';
        } else {
            debugWarn(`[SKIN-QUIZ-NEW] Sidebar item count element .itemcount${key} not found.`);
        }
    }
    
    // Update total price display (one shared cents->dollars helper).
    const totalStr = window.SkinExp.formatMoneyFromCents(total);
    const totalElement = document.querySelector('.custom-total');
    if (totalElement) {
      totalElement.textContent = totalStr;
    }

    // Sync bottom bar total price
    var bottomBarTotalPrice = document.getElementById('bottomBarTotalPrice');
    if (bottomBarTotalPrice) {
      bottomBarTotalPrice.textContent = totalStr;
    }

    // Update "Add to Cart" button style
    const buttonCustom = document.querySelector('.button-custom');
    if (buttonCustom) {
        if (document.querySelectorAll('.custom-input:checked').length > 0) {
            buttonCustom.style.backgroundColor = '#000';
            buttonCustom.style.color = '#fff';
        } else {
            buttonCustom.style.backgroundColor = '#ccc';
            buttonCustom.style.color = '#000';
        }
    }

    // Store selected products in localStorage
    localStorage.setItem(STORAGE_KEYS.SELECTED_PRODUCTS, JSON.stringify(selectedProducts));

    // Bundle offer progress. Count ELIGIBLE ITEMS — not distinct products, and
    // not products the discount excludes — because that is exactly what
    // Shopify's minimum counts.
    updateBundleOffer(window.SkinExp.bundleEligibleCount(bundleUnits));

    debugLog(' Updated item counts:', stepQuantities);
    debugLog(' Updated total price:', formatMoney(total.toString()));
    debugLog(' Selected products for cart:', selectedProducts);

    return total;
  }
  
  // Function to toggle between day and night products
  function toggleDayNight(button, time, stepName) {
    // Remove active class from all buttons in this group
    const buttonGroup = button.parentElement;
    buttonGroup.querySelectorAll('.toggle-btn').forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-pressed', 'false');
    });

    // Add active class to clicked button
    button.classList.add('active');
    button.setAttribute('aria-pressed', 'true');

    // Apply enhanced icon/button styling
    if (typeof updateToggleStyles === 'function') {
      updateToggleStyles(buttonGroup, time);
    }
    
    // Toggle product visibility
    const amProducts = document.querySelector(`.${stepName}-am-products`);
    const pmProducts = document.querySelector(`.${stepName}-pm-products`);
    
    if (amProducts && pmProducts) {
      // Unslick both sliders first if they are initialized
      if ($(amProducts).hasClass('slick-initialized')) {
        $(amProducts).slick('unslick');
      }
      if ($(pmProducts).hasClass('slick-initialized')) {
        $(pmProducts).slick('unslick');
      }
      
      // Toggle display
      amProducts.style.display = time === 'am' ? 'block' : 'none';
      pmProducts.style.display = time === 'pm' ? 'block' : 'none';
      
      // Reinitialize the visible slider using shared config
      const activeSlider = time === 'am' ? amProducts : pmProducts;
      if (activeSlider.children.length > 0) {
        $(activeSlider).slick(SLICK_CONFIG);
      } else {
        debugLog(`No products in ${time} slider for ${stepName}, skipping Slick init.`);
      }

      // Update UI after toggling and re-initializing slider
      const accordionItem = button.closest('.accordion-custom-item');
      const contentPanel = accordionItem?.querySelector('.accordion-custom-content');
      if (contentPanel) {
          // safeUpdateCartTotal(); // OLD CALL
          // safeUpdateItemCount(contentPanel); // OLD CALL
          refreshAllSkincareStepUI(); // NEW CALL
      } else {
          debugWarn(' Could not find content panel after day/night toggle.');
          refreshAllSkincareStepUI(); // Still try to refresh global state
      }

      // The visible grid just changed, so this step's "is anything chosen here"
      // answer may have flipped: open it again if the newly shown grid is empty.
      if (accordionItem) { syncStepCollapse(accordionItem); }
    }
  }
  
  // Valid values for skin quiz profile fields
  const VALID_SKIN_TYPES = ['Dry', 'Oily', 'Combination', 'Normal'];
  const VALID_SENSITIVITY = ['Sensitive', 'Not Sensitive'];
  const VALID_CONCERNS = [
    'Acne-Prone', 'Hyperpigmentation', 'Redness/Rosacea',
    'Fine Lines/Wrinkles', 'Dark Circles', 'Dehydration',
    'Uneven Texture', 'Dullness'
  ];

  // Validate and sanitize the quiz profile data
  function validateQuizProfile(data) {
    if (!data || typeof data !== 'object') {
      debugWarn('Quiz profile data is not a valid object');
      return null;
    }

    // Create a sanitized copy
    const sanitized = {};

    // Validate skin type (required)
    if (data.skinType && VALID_SKIN_TYPES.includes(data.skinType)) {
      sanitized.skinType = data.skinType;
    } else {
      debugWarn('Invalid or missing skinType in quiz profile:', data.skinType);
      return null; // Skin type is required
    }

    // Validate sensitivity (required)
    if (data.sensitivity && VALID_SENSITIVITY.includes(data.sensitivity)) {
      sanitized.sensitivity = data.sensitivity;
    } else {
      // Default to 'Not Sensitive' if invalid
      sanitized.sensitivity = 'Not Sensitive';
    }

    // Validate primary concern (optional)
    if (data.primaryConcern && VALID_CONCERNS.includes(data.primaryConcern)) {
      sanitized.primaryConcern = data.primaryConcern;
    }

    // Validate concerns array (optional)
    if (Array.isArray(data.concerns)) {
      sanitized.concerns = data.concerns.filter(c => VALID_CONCERNS.includes(c));
    }

    // Validate name (optional, sanitize string)
    if (data.name && typeof data.name === 'string') {
      // Limit name length and remove potentially harmful characters
      sanitized.name = data.name.substring(0, 100).replace(/[<>'"&]/g, '');
    }

    // Validate isPregnant (optional, boolean)
    if (typeof data.isPregnant === 'boolean') {
      sanitized.isPregnant = data.isPregnant;
    }

    // Validate sunscreen preferences (optional)
    if (data.sunscreenPrefs && typeof data.sunscreenPrefs === 'object') {
      const validTypes = ['Physical', 'Chemical', 'NoPreference'];
      const validColors = ['Tinted', 'Untinted'];

      sanitized.sunscreenPrefs = {};
      if (validTypes.includes(data.sunscreenPrefs.type)) {
        sanitized.sunscreenPrefs.type = data.sunscreenPrefs.type;
      }
      if (validColors.includes(data.sunscreenPrefs.color)) {
        sanitized.sunscreenPrefs.color = data.sunscreenPrefs.color;
      }
    }

    return sanitized;
  }

  // Function to load skin quiz data if available
  function loadSkinQuizData() {
    let savedProfile;

    try {
      savedProfile = localStorage.getItem(STORAGE_KEYS.QUIZ_PROFILE);
    } catch (storageError) {
      debugError('Error accessing localStorage:', storageError);
      return null;
    }

    if (savedProfile) {
      try {
        const profileData = JSON.parse(savedProfile);

        // Validate the data structure and values
        const validatedData = validateQuizProfile(profileData);

        if (validatedData) {
          debugLog('Skin profile data loaded and validated:', validatedData);
          updatePageHeader(validatedData);
          return validatedData;
        } else {
          debugWarn('Quiz profile failed validation, clearing invalid data');
          // Optionally clear invalid data
          try {
            localStorage.removeItem(STORAGE_KEYS.QUIZ_PROFILE);
          } catch (e) {
            debugError('Could not clear invalid profile data:', e);
          }
          return null;
        }
      } catch (parseError) {
        debugError('Error parsing skin quiz profile JSON:', parseError);
        // Clear corrupted data
        try {
          localStorage.removeItem(STORAGE_KEYS.QUIZ_PROFILE);
        } catch (e) {
          debugError('Could not clear corrupted profile data:', e);
        }
        return null;
      }
    }
    return null;
  }
  
  // Update the page header with skin profile info
  function updatePageHeader(profileData) {
    if (!profileData) return;

    // Update the new results header title with user's name. FIRST word only:
    // testers typing notes into the name field ("Jonathan oily acne") shipped
    // titles like "Jonathan oily acne's Routine".
    const resultsTitle = document.getElementById('resultsUserName');
    if (resultsTitle) {
      const firstName = String(profileData.name || '').trim().split(/\s+/)[0] || '';
      resultsTitle.textContent = firstName ? `${firstName}'s Routine` : 'Your Routine';
    }

    // Name the concern in the sidebar note. Nothing filled this in, so the
    // sentence shipped reading "Based on your goal to address your concerns"
    // no matter what the shopper answered.
    const concernEl = document.getElementById('userConcern');
    if (concernEl) {
      const concerns = Array.isArray(profileData.concerns) ? profileData.concerns.filter(Boolean) : [];
      const primary = profileData.primaryConcern || concerns[0];
      if (primary) {
        const extra = concerns.filter(c => c !== primary);
        concernEl.textContent = extra.length
          ? `${primary} and ${extra.join(', ')}`
          : primary;
      }
    }

    // Update the title to be more personalized (legacy support)
    const descriptionElement = document.querySelector('.custom-description');
    if (descriptionElement) {
      const titleElement = descriptionElement.querySelector('h1');
      const descElement = descriptionElement.querySelector('p');

      if (titleElement) {
        titleElement.textContent = `${profileData.name || 'Your'} Personalized Skincare Routine`;
      }

      if (descElement) {
        const skinType = profileData.skinType || '';
        // Correctly access concerns which might be an array or string
        let concernsText = '';
        if (profileData.concerns) {
            if (Array.isArray(profileData.concerns)) {
                concernsText = profileData.concerns.join(', ');
            } else if (typeof profileData.concerns === 'string') {
                concernsText = profileData.concerns;
            }
        }

        descElement.innerHTML = `Based on your <strong>${skinType}</strong> skin type${concernsText ? ` and ${concernsText} concerns` : ''}, we've created the perfect routine. Toggle between day and night to see recommendations for each time of day.`;
      }
    }
  }
  
  // Escape HTML to prevent XSS attacks
  // Turn "&amp;" / "&#39;" back into "&" / "'". Product copy arrives from Liquid
  // already entity-encoded; without this it gets encoded a second time and the
  // entity itself shows up in the card.
  function decodeEntities(text) {
    if (text == null) return '';
    const s = String(text);
    if (s.indexOf('&') === -1) return s;
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  }

  function escapeHtml(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    // innerHTML escapes & < >, but NOT quotes — and this helper is used inside
    // double-quoted attributes (alt, data-product-title). A product name
    // containing a " would otherwise break out of the attribute.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Format money - handles both cents (integer) and formatted strings
  function formatMoney(amount) {
    // Handle null/undefined
    if (amount == null) return '$0.00';

    let cents;

    if (typeof amount === 'string') {
      // Remove currency symbols and whitespace
      const cleaned = amount.replace(/[$,\s]/g, '');

      // Check if it's a decimal string like "21.00" or integer string like "2100"
      if (cleaned.includes('.')) {
        // It's a formatted price like "21.00" - convert to cents
        cents = Math.round(parseFloat(cleaned) * 100);
      } else {
        // It's already in cents like "2100"
        cents = parseInt(cleaned, 10);
      }
    } else if (typeof amount === 'number') {
      // Input is always integer cents (R12). No <1000 heuristic.
      cents = Math.round(amount);
    } else {
      cents = 0;
    }

    // Ensure it's a valid number
    if (isNaN(cents)) cents = 0;

    const dollars = Math.floor(Math.abs(cents) / 100);
    const centsPart = (Math.abs(cents) % 100).toString().padStart(2, '0');
    const sign = cents < 0 ? '-' : '';

    return `${sign}$${dollars}.${centsPart}`;
  }

  // Update the item count in the sidebar and calculate total price 
  // RENAMED and REPURPOSED as the main UI refresh function
  function refreshAllSkincareStepUI() {
    debugLog(' Executing refreshAllSkincareStepUI');
    let total = 0;
    const selectedProductsForCart = {}; // For potential cart submission
    const stepQuantities = {}; // E.g., { "1-am": 0, "1-pm": 0, "2": 0, ... }
    const countedVariantsRefresh = new Set(); // Track variant IDs to avoid double-counting AM/PM
    // { quantity, eligible } per selection, for the bundle offer counter.
    const bundleUnits = [];

    // Initialize all potential step counters to 0 by querying them from the DOM
    document.querySelectorAll('[class*="itemcount"]').forEach(el => {
      const match = el.className.match(/itemcount([\w-]+)/);
      if (match && match[1]) {
        if (!(match[1] in stepQuantities)) {
            stepQuantities[match[1]] = 0;
        }
      }
    });

    document.querySelectorAll('.custom-input:checked').forEach(checkbox => {
      const accordionCustomItem = checkbox.closest('.accordion-custom-item');
      if (!accordionCustomItem) {
        debugWarn(' Checkbox found without parent .accordion-custom-item:', checkbox);
        return;
      }

      const h2 = accordionCustomItem.querySelector('.accordion h2');
      if (!h2) {
        debugWarn(' .accordion-custom-item found without h2:', accordionCustomItem);
        return;
      }

      const stepText = h2.textContent.trim();
      const stepNumberMatch = stepText.match(/^(\d+)\./);
      if (!stepNumberMatch) {
        debugWarn(' Step H2 text does not start with number:', stepText);
        return;
      }
      const stepNumber = stepNumberMatch[1];
      const stepName = stepText.substring(stepText.indexOf('.') + 1).trim();

      const price = parseFloat(checkbox.getAttribute('product-price') || 0);
      const quantityInput = checkbox.closest('label')?.querySelector('.quantity-input');
      const quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1;

      const variantId = checkbox.value;

      // Count each variant ONCE — see the matching note in updateItemCount.
      if (countedVariantsRefresh.has(variantId)) { return; }
      countedVariantsRefresh.add(variantId);

      total += price * quantity;

      // The sidebar renders ONE count badge per step (.itemcountN), so AM and PM
      // selections for a step must aggregate into the same key. The old cleanser
      // AM/PM suffix produced key "1-am", which matched no .itemcount badge — that
      // is exactly why the Cleanser sidebar count never moved off 0.
      // The step badge counts what is chosen in the routine ON SCREEN, not in
      // both at once. Morning and evening are separate choices, so a morning
      // pick used to leave the badge reading "1" in the evening view where the
      // shopper had chosen nothing — telling them a step was handled when it
      // was not. The TOTAL and the cart deliberately still count everything:
      // both picks are real and both get bought.
      const gridOfCheckbox = checkbox.closest('.rp-product-grid');
      const inVisibleGrid = !gridOfCheckbox || getComputedStyle(gridOfCheckbox).display !== 'none';
      if (inVisibleGrid) {
        const fullStepKey = `${stepNumber}`;
        if (fullStepKey in stepQuantities) {
          stepQuantities[fullStepKey] += quantity;
        } else {
          stepQuantities[fullStepKey] = quantity;
          debugWarn(`[SKIN-QUIZ-NEW] Encountered new step key during processing: ${fullStepKey}. Initializing.`);
        }
      }

      if (variantId) {
        selectedProductsForCart[variantId] = quantity;
      }

      bundleUnits.push({
        priceCents: price,
        quantity: quantity,
        eligible: checkbox.getAttribute('data-bundle-eligible') !== 'false'
      });
    });
    
    // Update step count displays in the sidebar
    for (const key in stepQuantities) {
        const itemCountElement = document.querySelector(`.itemcount${key}`);
        if (itemCountElement) {
            // A step that belongs to the other routine shows "AM only"/"PM only"
            // here instead of a number. Do not stamp a 0 over it.
            if (itemCountElement.classList.contains('is-tag')) { continue; }
            const currentQuantity = stepQuantities[key];
            itemCountElement.textContent = currentQuantity;
            // Only show the badge once there is something in the step. `0` on
            // every row reads as clutter and says nothing.
            itemCountElement.classList.toggle('has-count', currentQuantity > 0);
            itemCountElement.style.backgroundColor = currentQuantity > 0 ? 'var(--se-success)' : '#efefef';
            itemCountElement.style.color = currentQuantity > 0 ? '#fff' : '#000';
        } else {
            debugWarn(`[SKIN-QUIZ-NEW] Sidebar item count element .itemcount${key} not found.`);
        }
    }

    // Totals. Once the bundle qualifies these show the old price struck through
    // beside the new one, so the saving is visible where the shopper is already
    // looking rather than only in the line underneath.
    const bundleTotals = window.SkinExp.bundleTotals(bundleUnits, getBundleConfig());
    window.SkinExp.renderBundleTotal(document.querySelector('.custom-total'), bundleTotals);
    window.SkinExp.renderBundleTotal(document.getElementById('bottomBarTotalPrice'), bundleTotals);

    // Update "Add to Cart" button style
    const buttonCustom = document.querySelector('.button-custom');
    if (buttonCustom) {
        if (document.querySelectorAll('.custom-input:checked').length > 0) {
            buttonCustom.style.backgroundColor = '#000';
            buttonCustom.style.color = '#fff';
        } else {
            buttonCustom.style.backgroundColor = '#ccc';
            buttonCustom.style.color = '#000';
        }
    }

    // Store selected products in localStorage
    localStorage.setItem(STORAGE_KEYS.SELECTED_PRODUCTS, JSON.stringify(selectedProductsForCart));

    // Bundle offer progress. This function — not updateItemCount, which only
    // runs when restoring a saved routine — is what every selection goes
    // through, so the offer line has to be driven from here or it never speaks.
    updateBundleOffer(bundleTotals.eligibleCount);

    // Update sticky cart bar visibility and count
    if (typeof updateStickyCartBar === 'function') {
      updateStickyCartBar();
    }

    debugLog(' Refreshed item counts:', stepQuantities);
    debugLog(' Refreshed total price:', formatMoney(total.toString()));
    debugLog(' Refreshed products for cart (localStorage):', selectedProductsForCart);

    return total;
  }

  // Debounced version of refreshAllSkincareStepUI for better performance
  // Waits 150ms after last call before executing
  const debouncedRefreshUI = debounce(refreshAllSkincareStepUI, 150);

  // Initialize: load product data when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadProductData);
  } else {
    loadProductData();
  }

  // Mobile sticky bar: track which step is currently in view
  function initMobileBarStepTracker() {
    var mobileBarStep = document.getElementById('mobileBarStep');
    if (!mobileBarStep) return;

    var steps = document.querySelectorAll('.accordion-custom');
    if (!steps.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var heading = entry.target.querySelector('.accordion h2');
          if (heading) {
            mobileBarStep.textContent = heading.textContent.trim();
          }
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    });

    steps.forEach(function(step) {
      observer.observe(step);
    });
  }

  // Init the step tracker after products load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(initMobileBarStepTracker, 1000);
    });
  } else {
    setTimeout(initMobileBarStepTracker, 1000);
  }

// ============================================
// FEATURE: Close Routine Builder (X button)
// ============================================
function closeRoutineBuilder() {
  // Leave the page, don't hide the overlay in place.
  //
  // Hiding it stranded the shopper: this page's own content IS the overlay, the
  // theme header is hidden on these surfaces, and the page's main-page section
  // is disabled — so dismissing the overlay left a blank viewport, the footer,
  // and a still-live "TOTAL ESTIMATE: $0.00 / ADD TO CART" bar with nothing
  // behind it. Same treatment as the routine builder and the quiz: go back
  // within the site, otherwise home.
  document.body.style.overflow = '';
  if (document.referrer && document.referrer.indexOf(window.location.hostname) !== -1) {
    window.history.back();
  } else {
    window.location.href = '/';
  }
}

// Initialize close button handler
function initCloseButton() {
  var closeBtn = document.querySelector('.ebc-close-btn');
  if (closeBtn) {
    closeBtn.addEventListener('click', function(e) {
      e.preventDefault();
      closeRoutineBuilder();
    });
    debugLog('Close button handler attached');
  }

  // This surface is a role="dialog" takeover, so Escape should leave it — the
  // convention every other modal on the web follows.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    // Don't fight the theme's cart drawer or the quick-view modal if one is open.
    if (document.body.classList.contains('cart-sidebar-show')) return;
    if (document.querySelector('.rp-quickview-modal.is-open')) return;
    closeRoutineBuilder();
  });
}

// ============================================
// FEATURE: Enhanced AM/PM Toggle with Icons
// ============================================
// The selected segment is styled by `.toggle-btn.active` in
// section-skin-quiz-new.css (ink fill, white label and icon), matching the
// segmented control on the routine landing pages.
//
// This used to paint the buttons with inline styles instead, which beat the
// stylesheet: Day-selected got a WHITE background with gold #b8860b text —
// visually identical to an unselected button — while Night-selected got a
// black fill. So only one of the two states looked selected. All this does now
// is strip any inline paint left over from that, so the class decides.
function updateToggleStyles(buttonGroup) {
  if (!buttonGroup) return;
  buttonGroup.querySelectorAll('.toggle-btn').forEach(function (btn) {
    btn.style.removeProperty('background-color');
    btn.style.removeProperty('color');
    var icon = btn.querySelector('.sun-icon, .moon-icon');
    if (icon) { icon.style.removeProperty('stroke'); }
  });
}

// ============================================
// FEATURE: Desktop Sidebar Step Navigation
// ============================================
var currentActiveStep = 0;

// Highlight the step the shopper is looking at.
//
// The sidebar's CLICK behaviour lives in sections/custom-quiz.liquid (one
// delegated handler that both highlights and jumps). This function only keeps
// the highlight in sync when the shopper scrolls, so it must not fight that
// handler by scrolling as well.
//
// It used to query '#routine-categories > div' — the nav is rebuilt as
// <button> elements, so that selector matched nothing and this whole path was
// dead. It also painted the active item with inline styles in #b8860b gold,
// which beat the stylesheet and did not match the palette; the .is-active class
// already carries the correct treatment.
function setActiveStep(stepIndex) {
  currentActiveStep = stepIndex;

  var sidebarSteps = document.querySelectorAll('#routine-categories .ebc-step-nav__item');
  sidebarSteps.forEach(function (step, idx) {
    var on = idx === stepIndex;
    step.classList.toggle('is-active', on);
    if (on) { step.setAttribute('aria-current', 'step'); }
    else { step.removeAttribute('aria-current'); }
  });

  updateMobileStepIndicator(stepIndex);

  debugLog('Active step set to:', stepIndex);
}

// ============================================
// FEATURE: Mobile Horizontal Step Scroll
// ============================================
function updateMobileStepIndicator(stepIndex) {
  var mobileStepTabs = document.querySelectorAll('.mobile-step-tab');
  mobileStepTabs.forEach(function(tab, idx) {
    var on = idx === stepIndex;
    // `is-active` is what the stylesheet and the delegated tab handler both use;
    // `active` is kept for any older rule still matching on it. The inline
    // #b8860b gold that used to be painted here beat the stylesheet and was not
    // a colour in this palette.
    tab.classList.toggle('is-active', on);
    tab.classList.toggle('active', on);
    tab.style.backgroundColor = '';
    tab.style.color = '';
    if (on) {
      tab.setAttribute('aria-current', 'step');
      // Keep the selected tab visible in the horizontal strip. `inline` only
      // moves the strip sideways, so this cannot fight the vertical jump.
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    } else {
      tab.removeAttribute('aria-current');
    }
  });

  // Also update the mobile bar step text
  var mobileBarStep = document.getElementById('mobileBarStep');
  var accordionItems = document.querySelectorAll('.accordion-custom');
  if (mobileBarStep && accordionItems[stepIndex]) {
    var heading = accordionItems[stepIndex].querySelector('.accordion h2');
    if (heading) {
      mobileBarStep.textContent = heading.textContent.trim();
    }
  }
}

function initMobileStepScroll() {
  var mobileStepContainer = document.querySelector('.mobile-step-tabs');
  if (mobileStepContainer) {
    // Add scroll snap behavior via JS (CSS should also have it)
    mobileStepContainer.style.scrollSnapType = 'x mandatory';
    mobileStepContainer.style.overflowX = 'auto';
    mobileStepContainer.style.webkitOverflowScrolling = 'touch';

    var tabs = mobileStepContainer.querySelectorAll('.mobile-step-tab');
    tabs.forEach(function(tab, idx) {
      tab.style.scrollSnapAlign = 'center';
      tab.style.cursor = 'pointer';
      // Add click handler to navigate to step
      tab.addEventListener('click', function() {
        setActiveStep(idx);
      });
    });
  }

  // Use IntersectionObserver to detect which step is in view
  var accordionItems = document.querySelectorAll('.accordion-custom');
  if (accordionItems.length === 0) return;

  var stepObserver = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
        var stepIndex = Array.from(accordionItems).indexOf(entry.target);
        if (stepIndex !== -1 && stepIndex !== currentActiveStep) {
          // One place decides what "active" looks like. This used to inline
          // scale/border/shadow onto '#routine-categories > div', a selector that
          // matches nothing since the nav became buttons.
          setActiveStep(stepIndex);
        }
      }
    });
  }, {
    rootMargin: '-20% 0px -50% 0px',
    threshold: [0.1, 0.3, 0.5]
  });

  accordionItems.forEach(function(item) {
    stepObserver.observe(item);
  });

  debugLog('Mobile step scroll initialized with IntersectionObserver');
}

// ============================================
// FEATURE: Sticky Cart Bar Logic
// ============================================
var stickyCartBarVisible = false;

function showStickyCartBar() {
  var stickyBar = document.querySelector('.sticky-cart-bar, .mobile-sticky-bar');
  if (stickyBar && !stickyCartBarVisible) {
    stickyBar.style.transform = 'translateY(0)';
    stickyBar.style.opacity = '1';
    stickyBar.style.visibility = 'visible';
    stickyBar.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
    stickyCartBarVisible = true;
    debugLog('Sticky cart bar shown');
  }
}

function hideStickyCartBar() {
  var stickyBar = document.querySelector('.sticky-cart-bar, .mobile-sticky-bar');
  if (stickyBar && stickyCartBarVisible) {
    stickyBar.style.transform = 'translateY(100%)';
    stickyBar.style.opacity = '0';
    // Use transitionend to set visibility after animation
    stickyBar.addEventListener('transitionend', function handler() {
      if (!stickyCartBarVisible) {
        stickyBar.style.visibility = 'hidden';
      }
      stickyBar.removeEventListener('transitionend', handler);
    });
    stickyCartBarVisible = false;
    debugLog('Sticky cart bar hidden');
  }
}

function updateStickyCartBar() {
  var checkedItems = document.querySelectorAll('.custom-input:checked');
  var itemCount = checkedItems.length;

  // Update item count badge
  var countBadge = document.querySelector('.cart-item-count, .sticky-cart-count');
  if (countBadge) {
    countBadge.textContent = itemCount;
  }

  // Show/hide based on selection
  if (itemCount > 0) {
    showStickyCartBar();
  } else {
    hideStickyCartBar();
  }
}

function initStickyCartBar() {
  var stickyBar = document.querySelector('.sticky-cart-bar, .mobile-sticky-bar');
  if (stickyBar) {
    // Set initial state (hidden)
    stickyBar.style.transform = 'translateY(100%)';
    stickyBar.style.opacity = '0';
    stickyBar.style.visibility = 'hidden';
    stickyBar.style.position = 'fixed';
    stickyBar.style.bottom = '0';
    stickyBar.style.left = '0';
    stickyBar.style.right = '0';
    stickyBar.style.zIndex = '1000';
  }
  debugLog('Sticky cart bar initialized');
}

// ============================================
// FEATURE: Product Card Hover Effects
// ============================================
function initProductCardHoverEffects() {
  // Add overflow:hidden to product card containers for image zoom effect
  var productCards = document.querySelectorAll('.rp-product-card__image-wrap');
  productCards.forEach(function(card) {
    card.style.overflow = 'hidden';

    var img = card.querySelector('img');
    if (img) {
      img.style.transition = 'transform 0.3s ease';

      card.addEventListener('mouseenter', function() {
        img.style.transform = 'scale(1.05)';
      });

      card.addEventListener('mouseleave', function() {
        img.style.transform = 'scale(1)';
      });
    }
  });
  debugLog('Product card hover effects initialized');
}

// Re-initialize hover effects when new products are loaded
function reinitProductCardHoverEffects() {
  // Use MutationObserver to watch for new product cards
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            var cards = node.querySelectorAll ? node.querySelectorAll('.rp-product-card__image-wrap') : [];
            cards.forEach(function(card) {
              card.style.overflow = 'hidden';
              var img = card.querySelector('img');
              if (img) {
                img.style.transition = 'transform 0.3s ease';
                card.addEventListener('mouseenter', function() {
                  img.style.transform = 'scale(1.05)';
                });
                card.addEventListener('mouseleave', function() {
                  img.style.transform = 'scale(1)';
                });
              }
            });
          }
        });
      }
    });
  });

  var container = document.getElementById('routine-steps-container');
  if (container) {
    observer.observe(container, { childList: true, subtree: true });
  }
}

// ============================================
// ENHANCED: Update toggleDayNight with icon styles
// ============================================
// Wrap original toggleDayNight to add icon styling
var originalToggleDayNight = typeof toggleDayNight !== 'undefined' ? toggleDayNight : null;

// ============================================
// Initialize all new features
// ============================================
function initAllNewFeatures() {
  initCloseButton();
  // No initSidebarStepNavigation() — the sidebar's clicks are owned by the one
  // delegated handler in sections/custom-quiz.liquid, which both highlights and
  // jumps. The copy that used to live here bound to '#routine-categories > div'
  // and silently attached nothing.
  initMobileStepScroll();
  initStickyCartBar();
  initProductCardHoverEffects();
  reinitProductCardHoverEffects();
  initToggleButtonStyles();

  // Initial sticky cart bar state check
  updateStickyCartBar();

  debugLog('All new features initialized');
}

// Initialize toggle button styles on page load
function initToggleButtonStyles() {
  var toggleGroups = document.querySelectorAll('.day-night-toggle');
  toggleGroups.forEach(function(group) {
    var activeBtn = group.querySelector('.toggle-btn.active');
    if (activeBtn) {
      var isAM = activeBtn.classList.contains('am-btn');
      updateToggleStyles(group, isAM ? 'am' : 'pm');
    }
  });
  debugLog('Toggle button styles initialized');
}

// Run initialization after DOM is ready and products are loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    setTimeout(initAllNewFeatures, 1500); // Wait for products to load
  });
} else {
  setTimeout(initAllNewFeatures, 1500);
}

// ============================================
// PUBLIC API - Expose necessary functions
// ============================================
// These functions need to be globally accessible for onclick handlers in HTML
window.SkinQuizResults = {
  toggleDayNight: toggleDayNight,
  refreshUI: refreshAllSkincareStepUI,
  formatMoney: formatMoney,
  // New feature functions
  closeRoutineBuilder: closeRoutineBuilder,
  setActiveStep: setActiveStep,
  updateStickyCartBar: updateStickyCartBar,
  showStickyCartBar: showStickyCartBar,
  hideStickyCartBar: hideStickyCartBar,
  // Debug utilities (only work when SKIN_QUIZ_DEBUG is true)
  debug: {
    log: debugLog,
    warn: debugWarn,
    error: debugError,
    enableDebug: function() {
      // Note: This won't actually enable debug since SKIN_QUIZ_DEBUG is const
      // To enable debugging, set SKIN_QUIZ_DEBUG = true at the top of the file
      debugLog('[SKIN-QUIZ-NEW] To enable debug mode, set SKIN_QUIZ_DEBUG = true at the top of section-skin-quiz-new.js');
    }
  }
};

/* Results UI safety net — runs after the page + any JS-injected content settle.
   Handles cases the Liquid guards can't see (a product whose featured_image is
   truthy but resolves to an empty URL, JS-injected cards, runtime 404s, and
   steps that end up with no products). */
(function resultsUiSafetyNet() {
  function fixImg(img) {
    var src = img.getAttribute('src') || '';
    var failed = src === '' || src === window.location.href || (img.complete && img.naturalWidth === 0);
    if (!failed) return;
    var card = img.closest('.rp-product-card, .custom-product-card, .product-spotlight');
    if (card && !card.classList.contains('img-failed')) { card.classList.add('img-failed'); }
    if (img.parentNode) { img.remove(); }
  }
  function patch() {
    document.querySelectorAll('#routine-steps-container img').forEach(function (img) {
      if (img.complete) { fixImg(img); }
      else {
        img.addEventListener('error', function () { fixImg(img); });
        img.addEventListener('load', function () { fixImg(img); });
      }
    });
    document.querySelectorAll('#routine-steps-container .accordion-custom-content').forEach(function (content) {
      var hasProducts = content.querySelector('label, .rp-product-card, .product-spotlight');
      var hasMsg = content.querySelector('.ebc-step-empty');
      if (!hasProducts && !hasMsg) {
        var p = document.createElement('p');
        p.className = 'ebc-step-empty';
        p.textContent = "We're curating options for this step — check back soon.";
        content.appendChild(p);
      }
    });
  }
  function ready(fn) {
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', fn); }
    else { fn(); }
  }
  // The carousel fades in only once Slick initialises (component behaviour).
  // If Slick never loads at all, reveal the cards as a plain stack after a
  // grace period rather than leaving steps that look empty. Runs once, late,
  // so it can't cause a flash of un-carouselled cards on a normal load.
  function slickFailsafe() {
    document.querySelectorAll('#routine-steps-container .rp-product-grid').forEach(function (grid) {
      if (!grid.classList.contains('slick-initialized') && grid.querySelector('label')) {
        grid.classList.add('rp-grid-visible');
      }
    });
  }
  ready(function () {
    patch();
    setTimeout(patch, 800);
    setTimeout(patch, 2000);
    setTimeout(slickFailsafe, 4000);
  });
})();


// ---------------------------------------------------------------------------
// Selected-routine strip (Aug 2026 meeting): thumbnails of everything the
// shopper has picked, pinned in the results header. Tapping a thumbnail jumps
// back to that product's step (same jump the step tabs use). Rebuilt from the
// checked inputs on every change, so it can never disagree with the cart math.
// ---------------------------------------------------------------------------
(function () {
  'use strict';

  function rebuildStrip() {
    var strip = document.getElementById('ebcRoutineStrip');
    if (!strip) return;
    var items = [];
    var seen = {};
    document.querySelectorAll('.custom-input:checked').forEach(function (input) {
      if (seen[input.value]) return;
      seen[input.value] = true;
      var card = input.closest('label');
      var img = card && card.querySelector('.rp-product-card__image');
      var accordion = input.closest('.ebc-accordion');
      items.push({
        src: img && img.src ? img.src : null,
        title: (input.getAttribute('data-product-title') || '').trim(),
        step: accordion ? accordion.getAttribute('data-step') : null
      });
    });

    if (items.length === 0) {
      strip.hidden = true;
      strip.innerHTML = '';
      return;
    }
    strip.hidden = false;
    strip.innerHTML = items.map(function (it) {
      var inner = it.src
        ? '<img src="' + it.src + '" alt="" loading="lazy">'
        : '<span class="ebc-routine-strip__fallback"></span>';
      return '<button type="button" class="ebc-routine-strip__item" data-step="' +
        (it.step || '') + '" title="' + it.title.replace(/"/g, '&quot;') + '" aria-label="Go back to ' +
        it.title.replace(/"/g, '&quot;') + '">' + inner + '</button>';
    }).join('');
  }

  document.addEventListener('change', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('custom-input')) {
      rebuildStrip();
    }
  });

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.ebc-routine-strip__item') : null;
    if (!btn) return;
    var step = btn.getAttribute('data-step');
    if (step && typeof window.__ebcJumpToStep === 'function') {
      window.__ebcJumpToStep(step);
    }
  });

  // Restored selections (localStorage) land after DOMContentLoaded — rebuild a
  // few times so the strip catches them without polling forever.
  document.addEventListener('DOMContentLoaded', function () {
    rebuildStrip();
    setTimeout(rebuildStrip, 800);
    setTimeout(rebuildStrip, 2000);
  });
})();
